import {
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState, useContext } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/InfoContext";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import useUserClaims from "../../hooks/useUserClaims";
import getBranchMembershipPlans from "../../api/services/prices/get_branch_membership";
import {
  createMembershipPlan,
  editMembershipPlan,
  deleteMembershipPlan,
} from "../../api/services/prices/set_branch_membership";
import getRequiredUserData from "../../utils/getBranchInfo";


const MembershipPlansGrid = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { openSnackbar } = useSnackbar();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const userClaims = useUserClaims(user);
  const branchData = getRequiredUserData();
  const [plansData, setPlansData] = useState([]);
  const [editMode, setEditMode] = useState({});
  const [newPlan, setNewPlan] = useState({
    plan_name: "",
    price: "",
    duration_months: "",
  });

  useEffect(() => {
    const fetchPlans = async () => {
      const branchId = branchData?.requiredId;
      try {
        const plans = await getBranchMembershipPlans(branchId);
        setPlansData(plans);
        setEditMode(
          plans.reduce(
            (acc, plan) => ({
              ...acc,
              [plan.id]: {
                plan_name: false,
                price: false,
                duration_months: false,
              },
            }),
            {}
          )
        );
      } catch (error) {
        console.error("Error fetching membership plans:", error);
      }
    };

    fetchPlans();
  }, [branchData?.requiredId]);

  const names = {
    plan_name: "Plan Name",
    price: "Price",
    duration_months: "Duration (Months)",
  };

  const handleInputChange = (planId, key, value) => {
    setPlansData((prevData) =>
      prevData.map((plan) =>
        plan.id === planId ? { ...plan, [key]: value } : plan
      )
    );
  };

  const handleNewPlanInputChange = (key, value) => {
    setNewPlan((prev) => ({ ...prev, [key]: value }));
  };

  const handleEditButtonClick = (planId, key) => {
    setEditMode((prevEditMode) => ({
      ...prevEditMode,
      [planId]: {
        ...prevEditMode[planId],
        [key]: true,
      },
    }));
  };

  const handleSaveButtonClick = async (planId, key) => {
    const plan = plansData.find((p) => p.id === planId);
    if (!plan || plan[key] === "") {
      openSnackbar(`${names[key]} cannot be empty!`, "error");
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    setEditMode((prevEditMode) => ({
      ...prevEditMode,
      [planId]: {
        ...prevEditMode[planId],
        [key]: false,
      },
    }));

    try {
      const updateData = { [key]: plan[key] };
      if (key === "price" || key === "duration_months") {
        updateData[key] = parseFloat(plan[key]);
      }
      await editMembershipPlan(
        user,
        updateData,
        branchData?.requiredId,
        planId
      );
      openSnackbar(`${names[key]} updated successfully!`, "success");
    } catch (error) {
      openSnackbar(error.message || "An unexpected error occurred.", "error");
    }
    setIsSubmitting(false);
  };

  const handleCreatePlan = async () => {
    if (!newPlan.plan_name || !newPlan.price || !newPlan.duration_months) {
      openSnackbar("All fields are required to create a plan!", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const branchId = branchData?.requiredId;
      const createdPlan = await createMembershipPlan(user, {
        plan_name: newPlan.plan_name,
        price: parseFloat(newPlan.price),
        duration_months: parseFloat(newPlan.duration_months),
        branchId,
      });
      setPlansData((prev) => [
        ...prev,
        {
          id: createdPlan.id,
          ...newPlan,
          price: parseFloat(newPlan.price),
          duration_months: parseFloat(newPlan.duration_months),
        },
      ]);
      setEditMode((prev) => ({
        ...prev,
        [createdPlan.id]: {
          plan_name: false,
          price: false,
          duration_months: false,
        },
      }));
      setNewPlan({ plan_name: "", price: "", duration_months: "" });
      openSnackbar("Plan created successfully!", "success");
    } catch (error) {
      openSnackbar(error.message || "An unexpected error occurred.", "error");
    }
    setIsSubmitting(false);
  };

  const handleDeletePlan = async (planId) => {
    setIsSubmitting(true);
    try {
      await deleteMembershipPlan(
        user,
        branchData?.requiredId,
        planId
      );
      setPlansData((prev) => prev.filter((plan) => plan.id !== planId));
      setEditMode((prev) => {
        const { [planId]: _, ...rest } = prev;
        return rest;
      });
      openSnackbar("Plan deleted successfully!", "success");
    } catch (error) {
      openSnackbar(error.message || "An unexpected error occurred.", "error");
    }
    setIsSubmitting(false);
  };

  return (
    <Paper
      elevation={5}
      style={{
        padding: "20px",
        margin: "20px",
        backgroundColor: theme.palette.background.alt,
        color: theme.palette.secondary[300],
      }}
    >
      <Typography
        variant="h4"
        align="center"
        style={{ fontWeight: "bold", marginBottom: "20px" }}
      >
        Membership Plans Set
      </Typography>

      {plansData.length === 0 ? (
        <Typography
          variant="h6"
          align="center"
          style={{ marginBottom: "20px" }}
        >
          No membership plans found
        </Typography>
      ) : (
        plansData.map((plan) => (
          <div key={plan.id} style={{ marginBottom: "40px" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <Typography variant="h6" style={{ fontWeight: "bold" }}>
                  Plan: {plan.plan_name || "Unnamed Plan"}
                </Typography>
              </Grid>
              <Grid item xs={4} style={{ textAlign: "right" }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleDeletePlan(plan.id)}
                  disabled={!(userClaims.superAdmin || userClaims.admin)}
                >
                  Delete Plan
                </Button>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              {Object.keys(names).map((key) => (
                <Grid item xs={12} md={6} key={`${plan.id}-${key}`}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={4} md={4}>
                      <Typography variant="subtitle1">{names[key]}</Typography>
                    </Grid>
                    <Grid item xs={4} md={4}>
                      <TextField
                        value={plan[key] || ""}
                        type={
                          key === "price" || key === "duration_months"
                            ? "number"
                            : "text"
                        }
                        onChange={(e) =>
                          handleInputChange(
                            plan.id,
                            key,
                            key === "price" || key === "duration_months"
                              ? Math.abs(parseFloat(e.target.value))
                              : e.target.value
                          )
                        }
                        disabled={
                          !editMode[plan.id]?.[key] ||
                          !(userClaims.superAdmin || userClaims.admin)
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={4} md={4}>
                      {editMode[plan.id]?.[key] ? (
                        <Button
                          variant="contained"
                          onClick={() => handleSaveButtonClick(plan.id, key)}
                        >
                          Save
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={() => handleEditButtonClick(plan.id, key)}
                          disabled={
                            !(userClaims.superAdmin || userClaims.admin)
                          }
                        >
                          Edit
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </div>
        ))
      )}

      {(userClaims.superAdmin || userClaims.admin) && (
        <div style={{ marginTop: "40px" }}>
          <Typography
            variant="h6"
            style={{ marginBottom: "20px", fontWeight: "bold" }}
          >
            Create New Plan
          </Typography>
          <Grid container spacing={2}>
            {Object.keys(names).map((key) => (
              <Grid item xs={12} md={6} key={`new-${key}`}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4} md={4}>
                    <Typography variant="subtitle1">{names[key]}</Typography>
                  </Grid>
                  <Grid item xs={8} md={8}>
                    <TextField
                      value={newPlan[key] || ""}
                      type={
                        key === "price" || key === "duration_months"
                          ? "number"
                          : "text"
                      }
                      onChange={(e) =>
                        handleNewPlanInputChange(
                          key,
                          key === "price" || key === "duration_months"
                            ? Math.abs(parseFloat(e.target.value))
                            : e.target.value
                        )
                      }
                      fullWidth
                      placeholder={`Enter ${names[key]}`}
                    />
                  </Grid>
                </Grid>
              </Grid>
            ))}
            <Grid item xs={12} style={{ textAlign: "right" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreatePlan}
              >
                Create Plan
              </Button>
            </Grid>
          </Grid>
        </div>
      )}
    </Paper>
  );
};

export default MembershipPlansGrid;
